// ---------------------------------------------------------
// Datei: FormularModal.tsx
// Zweck: Modal-Komponente für das Ausfüllen und Hochladen
//        von PDF-Formularen (VdZ, Inbetriebnahmeprotokoll)
// Kontext: Wird in DocumentChecklist verwendet, sendet Daten
//          an Make.com Webhook zur PDF-Generierung via pdf.co
// Stand: 2026-02-05 - Angepasst: signature_missing Variable
//        hinzugefügt für beide Formulare
// ---------------------------------------------------------
import React, { useState, useRef, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useQueue } from '../contexts/QueueContext';
import { useDealsStore } from '../store/dealsStore';
import { getDealByIdAsync as getDealById } from '../services/api/hubspot';

interface DealContact {
  firstname?: string;
  lastname?: string;
  phone?: string;
  mobilephone?: string;
  address?: string;
  city?: string;
  zip?: string;
}

interface FormularModalProps {
  isOpen: boolean;
  onClose: () => void;
  templatePath: string;
  documentTitle: string;
  dealId?: string;
  documentId?: string;
  phaseId?: string;
  onUploadComplete?: (fileId?: string) => void;
  dealContact?: DealContact;
}

/**
 * Konfiguration für PDF.co Inspector Koordinaten
 * Diese Werte stammen aus dem PDF.co Field Inspector
 * und definieren die exakte Position jedes Feldes im VdZ-PDF
 * 
 * WICHTIG: Die x,y-Koordinaten müssen exakt mit dem PDF übereinstimmen!
 * Bei Änderungen am PDF-Template müssen diese Werte neu ermittelt werden. 
 */
interface FieldConfig {
  pdfName: string;      // Beschreibung des Feldes
  x: number;            // X-Koordinate im PDF (von links)
  y: number;            // Y-Koordinate im PDF (von oben)
  w?: number;           // Breite des Feldes (optional)
  h?: number;           // Höhe des Feldes (optional)
  page: number;         // Seitennummer (0-basiert)
}

/**
 * VDZ Formular Feldkonfiguration
 * Mapping von HTML-Input-IDs zu PDF.co Koordinaten
 * 
 * Die Koordinaten wurden mit dem pdf.co Inspector ermittelt
 * und entsprechen dem offiziellen VdZ-Formular zur Bestätigung
 * des Hydraulischen Abgleichs
 */
const VDZ_FIELD_CONFIG: Record<string, FieldConfig> = {
    // ===========================================
    // Antragsteller-/Objektdaten (Seite 1, oben)
    // ===========================================
    'antragsteller':     { pdfName: 'Name/Antragsteller', x: 153.625, y: 163.067, w: 390.45, h: 14.90, page: 0 },
    'plz_ort_strasse':  { pdfName: 'PLZ/Ort/Straße', x: 153.625, y: 183.238, w: 390.12, h: 16.00, page: 0 },
    'objektanschrift':  { pdfName: 'Objektanschrift', x: 153.625, y: 204.537, w: 389.79, h: 16.00, page: 0 },

    // ===========================================
    // Verfahrenswahl (A oder B)
    // ===========================================
    'verfahren_a':      { pdfName: 'nach Verfahren A', x: 375.935, y: 297.665, w: 11.24, h: 11.44, page: 0 },
    'verfahren_b':       { pdfName: 'nach Verfahren B', x: 527.102, y: 297.665, w: 11.24, h: 11.44, page: 0 },
    
    // ===========================================
    // Ausdehnungsgefäß & Fülldruck
    // ===========================================
    'ausdehnung_geprueft': { pdfName:  'Ausdehnungsgefäß geprüft', x: 183.289, y: 333.304, w: 11.24, h: 11.44, page: 0 },
    'fuelldruck':           { pdfName: 'Fülldruck bar', x: 377.074, y: 333.558, w: 57.31, h: 11.22, page: 0 },

    // ===========================================
    // Heizkreis 1 - Systemart (Checkboxen)
    // ===========================================
    'hk1_zweirohr':     { pdfName: 'HK1 Zweirohrheizung', x: 298.894, y: 401.971, w: 11.24, h: 11.44, page: 0 },
    'hk1_fbh':          { pdfName:  'HK1 Fußbodenheizung', x: 298.894, y: 417.057, w: 11.24, h: 11.44, page: 0 },
    'hk1_einrohr':      { pdfName: 'HK1 Einrohrheizung', x: 298.894, y: 432.045, w: 11.24, h: 11.44, page: 0 },
    
    // Heizkreis 1 - Temperaturen & Durchfluss (Textfelder)
    'hk1_vorlauf':       { pdfName: 'HK1 Auslegungsvorlauftemperatur', x: 220.992, y: 449.053, w: 65.39, h: 11.22, page: 0 },
    'hk1_ruecklauf':    { pdfName:  'HK1 Heizkreisrücklauftemperatur', x: 220.992, y: 464.127, w: 65.39, h: 11.23, page: 0 },
    'hk1_durchfluss':   { pdfName:  'HK1 Gesamtdurchfluss', x: 220.992, y: 479.053, w: 65.39, h: 11.22, page: 0 },
    'hk1_pumpenhoehe':  { pdfName: 'HK1 Pumpenförderhöhe', x:  220.992, y: 494.114, w: 65.39, h: 11.22, page: 0 },
    
    // Heizkreis 1 - Regler (Checkboxen)
    'hk1_ddr':          { pdfName: 'HK1 Differenzdruckregler vorhanden', x: 282.019, y: 529.083, w: 11.36, h: 11.19, page: 0 },
    'hk1_dfr':          { pdfName: 'HK1 Durchflussregler vorhanden', x: 282.019, y: 557.109, w: 11.25, h: 11.19, page: 0 },

    // ===========================================
    // Heizkreis 2 - Systemart (Checkboxen)
    // ===========================================
    'hk2_zweirohr':     { pdfName: 'HK2 Zweirohrheizung', x: 412.406, y: 401.971, w: 11.24, h: 11.44, page: 0 },
    'hk2_fbh':          { pdfName: 'HK2 Fußbodenheizung', x: 412.406, y: 417.057, w: 11.24, h: 11.44, page: 0 },
    'hk2_einrohr':       { pdfName: 'HK2 Einrohrheizung', x: 412.406, y: 432.045, w: 11.24, h: 11.44, page: 0 },
    
    // Heizkreis 2 - Temperaturen & Durchfluss (Textfelder)
    'hk2_vorlauf':      { pdfName: 'HK2 Auslegungsvorlauftemperatur', x: 334.611, y: 449.107, w: 65.39, h: 11.22, page: 0 },
    'hk2_ruecklauf':     { pdfName: 'HK2 Heizkreisrücklauftemperatur', x: 334.611, y: 464.045, w: 65.39, h: 11.23, page: 0 },
    'hk2_durchfluss':    { pdfName: 'HK2 Gesamtdurchfluss', x: 334.611, y: 479.135, w: 65.39, h: 11.22, page: 0 },
    'hk2_pumpenhoehe':  { pdfName: 'HK2 Pumpenförderhöhe', x:  334.611, y: 494.087, w: 65.39, h: 11.22, page: 0 },
    
    // Heizkreis 2 - Regler (Checkboxen)
    'hk2_ddr':          { pdfName: 'HK2 Differenzdruckregler vorhanden', x: 395.433, y: 529.083, w: 11.25, h: 11.19, page: 0 },
    'hk2_dfr':           { pdfName: 'HK2 Durchflussregler vorhanden', x:  395.324, y: 557.109, w: 11.14, h: 11.52, page: 0 },

    // ===========================================
    // Heizkreis 3 - Systemart (Checkboxen)
    // ===========================================
    'hk3_zweirohr':     { pdfName: 'HK3 Zweirohrheizung', x: 525.843, y: 401.971, w: 11.24, h: 11.44, page: 0 },
    'hk3_fbh':          { pdfName: 'HK3 Fußbodenheizung', x: 525.843, y: 417.057, w: 11.24, h: 11.44, page: 0 },
    'hk3_einrohr':       { pdfName: 'HK3 Einrohrheizung', x: 525.843, y: 432.045, w: 11.24, h: 11.44, page: 0 },
    
    // Heizkreis 3 - Temperaturen & Durchfluss (Textfelder)
    'hk3_vorlauf':      { pdfName: 'HK3 Auslegungsvorlauftemperatur', x: 448.121, y: 449.189, w: 65.39, h: 11.22, page: 0 },
    'hk3_ruecklauf':     { pdfName: 'HK3 Heizkreisrücklauftemperatur', x: 448.121, y: 463.963, w: 65.39, h: 11.23, page: 0 },
    'hk3_durchfluss':    { pdfName: 'HK3 Gesamtdurchfluss', x: 448.039, y: 479.271, w: 65.39, h: 11.22, page: 0 },
    'hk3_pumpenhoehe':  { pdfName: 'HK3 Pumpenförderhöhe', x:  448.121, y: 494.250, w: 65.39, h: 11.22, page: 0 },
    
    // Heizkreis 3 - Regler (Checkboxen)
    'hk3_ddr':          { pdfName: 'HK3 Differenzdruckregler vorhanden', x: 508.576, y: 529.192, w: 11.25, h: 11.08, page: 0 },
    'hk3_dfr':          { pdfName: 'HK3 Durchflussregler vorhanden', x: 508.685, y: 556.891, w: 11.36, h: 11.85, page: 0 },

    // ===========================================
    // Bemerkungen & Bestätigungen
    // ===========================================
    'bemerkungen':       { pdfName: 'Bemerkungen', x: 60.238, y: 629.547, w: 482.82, h: 39.15, page: 0 },
    'doku_erhalten':    { pdfName: 'Dokumentation erhalten', x: 57.501, y: 768.310, w: 9.02, h: 8.86, page: 0 },

    // ===========================================
    // Unterschriften-Bereich (Ort, Datum)
    // ===========================================
    'ort_datum_fach':   { pdfName: 'Ort, Datum Fachbetrieb', x: 57.830, y: 729.629, w: 233.75, h: 16.61, page: 0 },
    'ort_datum_kunde':  { pdfName: 'Ort, Datum Antragsteller', x: 57.830, y: 785.955, w: 233.75, h: 17.59, page: 0 }
};


const FormularModal: React.FC<FormularModalProps> = ({
  isOpen, onClose, templatePath, documentTitle, dealId, documentId, phaseId, dealContact
}) => {
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { showToast } = useToast();
  const { addToQueue, isOnline } = useQueue();

  useEffect(() => {
    if (isOpen && templatePath) {
      loadTemplateWithData();
    }
  }, [isOpen, templatePath, dealId]);

  /**
   * Baut gespeicherte Werte direkt in den HTML-String ein
   * bevor er in den iframe geladen wird.
   *
   * Arbeitet rein auf String-Ebene — kein DOM-Zugriff nötig.
   */
  const prefillHtml = (html: string, data: Record<string, any>): string => {
    let result = html;

    for (const [key, value] of Object.entries(data)) {
      if (value === null || value === undefined || value === '') continue;

      // Regex: findet <input/textarea/select mit id="key" oder name="key">
      // Erfasst den gesamten Tag bis zum schließenden >
      const tagPattern = new RegExp(
        `(<(?:input|textarea|select)\\b[^>]*(?:id|name)=["']${key}["'][^>]*?)(/?>)`,
        'i'
      );

      const match = result.match(tagPattern);
      if (!match) continue;

      const tagContent = match[1];

      // Checkbox: checked-Attribut setzen/entfernen
      if (tagContent.includes('type="checkbox"') || tagContent.includes("type='checkbox'")) {
        if (value === true || value === 'true') {
          // checked einfügen (falls nicht schon vorhanden)
          if (!tagContent.includes('checked')) {
            result = result.replace(tagPattern, `$1 checked$2`);
          }
        }
        continue;
      }

      // Textarea: Inhalt zwischen den Tags setzen (value-Attribut hat keine Wirkung)
      if (/^<textarea\b/i.test(tagContent)) {
        const textareaPattern = new RegExp(
          `(<textarea\\b[^>]*(?:id|name)=["']${key}["'][^>]*>)[\\s\\S]*?(</textarea>)`,
          'i'
        );
        const escapedValue = String(value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        result = result.replace(textareaPattern, `$1${escapedValue}$2`);
        continue;
      }

      // Textfelder: value-Attribut setzen
      const safeValue = String(value).replace(/"/g, '&quot;');

      if (tagContent.includes('value=')) {
        // Bestehendes value-Attribut ersetzen
        result = result.replace(tagPattern, (fullMatch) => {
          return fullMatch.replace(/value=["'][^"']*["']/, `value="${safeValue}"`);
        });
      } else {
        // value-Attribut hinzufügen
        result = result.replace(tagPattern, `$1 value="${safeValue}"$2`);
      }
    }

    return result;
  };

  /**
   * Lädt Template und HubSpot-Daten in einem Schritt.
   * Füllt das HTML vor dem iframe-Laden vor — kein setTimeout-Hack nötig.
   */
  const loadTemplateWithData = async () => {
    setLoading(true);
    try {
      // 1. HTML-Template laden
      const response = await fetch(templatePath);
      if (!response.ok) throw new Error(`Template 404: ${templatePath}`);
      let html = await response.text();

      // 2. Wenn dealId vorhanden: gespeicherte Daten aus HubSpot holen
      if (dealId) {
        try {
          const isVDZ = documentTitle.toLowerCase().includes('hydraulisch') || documentTitle.toLowerCase().includes('vdz');
          const propertyName = isVDZ ? 'form_data_vdz' : 'form_data_ibn';

          let deal = useDealsStore.getState().deals.find(d => d.id === dealId);
          if (!deal) deal = await getDealById(dealId) ?? undefined;

          const formDataJson = deal?.[propertyName as 'form_data_vdz' | 'form_data_ibn'];
          if (formDataJson) {
            // Gespeicherte Formulardaten aus vorherigem Ausfüllen laden
            const formData = JSON.parse(formDataJson);
            html = prefillHtml(html, formData);
            console.log('[FORM] Formular vorausgefüllt mit', Object.keys(formData).length, 'Feldern');
          } else if (dealContact) {
            // Kein gespeicherter Stand → Kundendaten aus bereits geladenem Deal nutzen
            const c = dealContact;
            const fullName = [c.firstname, c.lastname].filter(Boolean).join(' ');
            const zipCity = [c.zip, c.city].filter(Boolean).join(' ');
            const fullAddress = [zipCity, c.address].filter(Boolean).join(', ');

            const prefillData: Record<string, string> = isVDZ
              ? {
                  antragsteller: fullName,
                  plz_ort_strasse: fullAddress,
                }
              : {
                  kunde: fullName,
                  adresse: fullAddress,
                  telefon: c.phone || c.mobilephone || '',
                };

            html = prefillHtml(html, prefillData);
            console.log('[FORM] Kundendaten vorausgefüllt:', prefillData);
          }
        } catch (error) {
          console.error('[FORM] HubSpot-Daten nicht verfügbar:', error);
          showToast('info', 'Vorherige Daten konnten nicht geladen werden', 3000);
        }
      }

      // 3. Fertiges HTML setzen (mit oder ohne Prefill)
      setHtmlContent(html);
    } catch (error) {
      console.error(error);
      showToast('error', 'Fehler beim Laden des Templates.', 4000);
      onClose();
    } finally { setLoading(false); }
  };

  /**
   * Extrahiert Formulardaten für PDF.co Annotation API
   * 
   * Diese Funktion wandelt die HTML-Formularwerte in das
   * pdf.co Format um, das exakte x,y-Koordinaten erfordert. 
   * 
   * Rückgabe: 
   * - annotations: Array von Textannotationen und Checkboxen
   * - signatures: Objekt mit base64-kodierten Unterschriftsbildern
   * - signatureMissing: true wenn KEINE Signaturen vorhanden sind
   */
  const extractForPDFForm = (doc: Document) => {
    const annotations: any[] = [];
    const signatures: Record<string, string> = {};

    // 1. Textfelder - erstelle Annotationsobjekte mit x,y Koordinaten
    doc.querySelectorAll('input:not([type="checkbox"]), textarea, select').forEach((el: any) => {
        const id = el.id || el.name;
        const config = VDZ_FIELD_CONFIG[id];
        
        // Nur Felder mit Konfiguration und Wert hinzufügen
        if (config && el.value && el.value.trim() !== '') {
            annotations.push({
                x: config.x.toString(),
                y: config.y.toString(),
                text: el.value.trim(),
                pages: config.page.toString(),
                width: config.w?.toString() || "100",
                height: config.h?.toString() || "12",
                size: "10",
                fontName: "Arial",
                type: "text"
            });
        }
    });

    // 2. Checkboxen - verwende checkboxChecked Typ für angehakte Boxen
    doc.querySelectorAll('input[type="checkbox"]').forEach((el: any) => {
        const id = el.id || el.name;
        const config = VDZ_FIELD_CONFIG[id];
        
        // Nur angehakte Checkboxen mit gültiger Konfiguration
        if (config && el.checked) {
            annotations.push({
                x: config.x.toString(),
                y: config.y.toString(),
                text: "X",  // oder "✓" je nach PDF-Stil
                pages: config.page.toString(),
                width: config.w?.toString() || "12",
                height: config.h?.toString() || "12",
                size: "10",
                fontName: "Arial",
                type: "text"  // Alternative: "checkboxChecked" falls pdf.co das unterstützt
            });
        }
    });

    // 3. Signaturen als Base64-kodierte PNG-Bilder extrahieren
    doc.querySelectorAll('canvas').forEach((canvas: HTMLCanvasElement) => {
       const id = canvas.id;
       // Nur nicht-leere Signaturen speichern
       const ctx = canvas.getContext('2d');
       if (ctx) {
           const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
           const hasContent = imageData.data.some((channel, index) => {
               // Prüfe Alpha-Kanal (jeder 4. Wert) auf nicht-transparente Pixel
               return index % 4 === 3 && channel > 0;
           });
           
           if (hasContent) {
               signatures[id] = canvas.toDataURL('image/png');
           }
       }
    });

    // NEU: Prüfe ob Signaturen fehlen
    const signatureMissing = Object.keys(signatures).length === 0;

    console.log('[PDF.co] Extrahierte Annotations:', annotations.length);
    console.log('[PDF.co] Extrahierte Signaturen:', Object.keys(signatures));
    console.log('[PDF.co] Signaturen fehlen:', signatureMissing);

    return { annotations, signatures, signatureMissing };
  };

  /**
   * Extrahiert alle Formularwerte als JSON-String
   * Wird für das Speichern in HubSpot verwendet
   */
  const extractFormData = (doc: Document): string => {
     const formData: Record<string, any> = {};
     doc.querySelectorAll('input, textarea, select').forEach((el: any) => {
        const id = el.id || el.name;
        if(id) {
            if(el.type === 'checkbox') formData[id] = el.checked;
            else formData[id] = el.value;
        }
     });
     return JSON.stringify(formData);
  };

  /**
   * Schreibt aktuelle Werte in HTML-Attribute
   * Notwendig für korrektes HTML-Rendering
   */
  const bakeValuesIntoAttributes = (doc: Document) => {
    doc.querySelectorAll('input, textarea, select').forEach((el: any) => {
        if(el.type === 'checkbox' || el.type === 'radio') {
            el.checked ? el.setAttribute('checked', 'checked') : el.removeAttribute('checked');
        } else if (el.tagName === 'TEXTAREA') {
            el.innerHTML = el.value;
        } else {
            el.setAttribute('value', el.value);
        }
    });
  };

  /**
   * Prüft ob im HTML-Dokument Signaturen vorhanden sind
   * für die HTML-Route (Inbetriebnahmeprotokoll)
   */
  const checkSignaturesInHTML = (doc: Document): boolean => {
    let hasSignatures = false;
    
    doc.querySelectorAll('canvas').forEach((canvas: HTMLCanvasElement) => {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hasContent = imageData.data.some((channel, index) => {
          return index % 4 === 3 && channel > 0;
        });
        
        if (hasContent) {
          hasSignatures = true;
        }
      }
    });
    
    return hasSignatures;
  };

  const validateForm = (doc: Document, isVDZ: boolean): string[] => {
    const errors: string[] = [];
    const checkText = (id: string, label: string) => {
      const el = doc.getElementById(id) as HTMLInputElement | null;
      if (!el || !el.value.trim()) errors.push(label);
    };
    if (isVDZ) {
      checkText('antragsteller', 'Name / Antragsteller');
      checkText('plz_ort_strasse', 'PLZ / Ort / Straße');
      const va = doc.getElementById('verfahren_a') as HTMLInputElement | null;
      const vb = doc.getElementById('verfahren_b') as HTMLInputElement | null;
      if (!va?.checked && !vb?.checked) errors.push('Verfahren A oder B');
    } else {
      checkText('kunde', 'Name des Kunden');
      checkText('adresse', 'Installationsadresse');
      checkText('modell', 'Wärmepumpenmodell');
      checkText('ibn', 'Inbetriebnahmedatum');
    }
    return errors;
  };

  /**
   * Hauptfunktion: Speichern und PDF generieren
   *
   * Unterscheidet zwischen VdZ (pdf.co Route) und anderen
   * Formularen (HTML-to-PDF Route)
   */
  const handleSavePDF = async () => {
    if (!iframeRef.current) return;

    const iframeDocCheck = iframeRef.current.contentDocument;
    if (!iframeDocCheck) return;
    const isVDZCheck = documentTitle.toLowerCase().includes('hydraulisch') || documentTitle.toLowerCase().includes('vdz');
    const validationErrors = validateForm(iframeDocCheck, isVDZCheck);
    if (validationErrors.length > 0) {
      showToast('error', `Pflichtfelder fehlen: ${validationErrors.join(', ')}`, 6000);
      return;
    }

    setSaving(true);

    try {
      const iframeDoc = iframeRef.current.contentDocument;
      if (!iframeDoc) throw new Error('Dokument konnte nicht geladen werden');

      const isVDZ = documentTitle.toLowerCase().includes('hydraulisch') || documentTitle.toLowerCase().includes('vdz');
      const formDataProperty = isVDZ ? 'form_data_vdz' : 'form_data_ibn';
      
      // Basis-Payload für Make.com Webhook
      let payload: any = {
        deal_id: dealId || 'unknown',
        document_title: documentTitle,
        form_data_property: formDataProperty,
        filename: `${documentTitle.replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.pdf`,
      };

      // Formular-Rohdaten für HubSpot-Speicherung
      payload.form_data_inputs = extractFormData(iframeDoc);

      if (isVDZ) {
        // =============================================
        // VDZ Route: PDF.co mit Annotations-Array
        // =============================================
        const { annotations, signatures, signatureMissing } = extractForPDFForm(iframeDoc);
        
        // pdf_fields enthält jetzt ein Array von Annotation-Objekten
        // mit exakten x,y Koordinaten für jedes Feld
        payload.pdf_fields = annotations;
        payload.signatures = signatures;
        payload.signature_missing = signatureMissing; // NEU: Flag für Make.com Flow
        payload.is_pdf_form = true;
        payload.html_content = "";

        console.log('[VDZ PAYLOAD] pdf_fields count:', annotations.length);
        console.log('[VDZ PAYLOAD] Signaturen:', Object.keys(signatures));
        console.log('[VDZ PAYLOAD] signature_missing:', signatureMissing);

      } else {
        // =============================================
        // HTML Route: Render komplettes HTML zu PDF
        // =============================================
        bakeValuesIntoAttributes(iframeDoc);
        
        // NEU: Prüfe ob Signaturen vorhanden sind
        const hasSignatures = checkSignaturesInHTML(iframeDoc);
        const signatureMissing = !hasSignatures;
        
        // Canvas-Elemente durch IMG-Tags ersetzen
        const canvases = iframeDoc.querySelectorAll('canvas');
        canvases.forEach((c: any) => {
            if(c.width > 0) {
               const img = iframeDoc.createElement('img');
               img.src = c.toDataURL('image/png');
               img.style.cssText = 'width: 100%; height:100px; object-fit:contain; background:#fff; display:block;';
               c.parentNode?.insertBefore(img, c);
               c.style.display='none';
            }
        });

        const fullHtml = `<!DOCTYPE html><html><head><style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 14px; }
          input, textarea, select { border: none; border-bottom: 1px solid #ccc; width: 100%; background: transparent; }
          .signature-controls, button { display: none !important; }
        </style></head><body>${iframeDoc.body.innerHTML}</body></html>`;
        
        payload.html_content = fullHtml;
        payload.signature_missing = signatureMissing; // NEU: Flag für Make.com Flow
        payload.is_pdf_form = false;

        console.log('[HTML PAYLOAD] signature_missing:', signatureMissing);
      }

      console.log('[PAYLOAD] Gesamter Payload:', JSON.stringify(payload, null, 2));

      // In Queue eintragen und sofort Modal schließen — Verarbeitung im Hintergrund
      addToQueue('make_pdf_upload', {
        dealId: payload.deal_id,
        documentTitle: payload.document_title,
        htmlContent: payload.html_content,
        filename: payload.filename,
        phaseId,
        documentId,
        pdfFields: payload.pdf_fields,
        signatures: payload.signatures,
        signatureMissing: payload.signature_missing,
        isPdfForm: payload.is_pdf_form,
        formDataInputs: payload.form_data_inputs,
        formDataProperty: payload.form_data_property,
      });

      if (isOnline) {
        showToast('info', 'Wird im Hintergrund synchronisiert...', 2000);
      } else {
        showToast('info', 'Offline: Wird synchronisiert sobald Internet da ist', 3000);
      }
      onClose();

    } catch (error) {
      console.error('[SAVE] Fehler:', error);
      showToast('error', 'Fehler beim Senden.', 4000);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" aria-busy={saving}>
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-slate-600">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{documentTitle}</h2>
            <button 
              onClick={onClose} 
              disabled={saving}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-xl font-bold p-2"
            >
              ✕
            </button>
        </div>
        <div className="flex-1 p-4 bg-gray-100 dark:bg-slate-700 overflow-hidden">
            <div className="relative w-full h-full">
              {loading ? (
                <div className="text-gray-700 dark:text-gray-300">Lade...</div>
              ) : (
                // Auf iOS/Safari können iframes über Overlays gemalt werden trotz z-index
                // Daher verstecken wir das iframe während des Uploads
                saving ? (
                  <div className="w-full h-full bg-white rounded shadow" aria-hidden="true" />
                ) : (
                  <iframe ref={iframeRef} srcDoc={htmlContent} className="w-full h-full bg-white rounded shadow" />
                )
              )}

              {saving && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/20 dark:bg-black/30 rounded" role="status" aria-live="polite">
                  <div className="flex items-center gap-3 px-4 py-3 bg-white/90 dark:bg-slate-800/90 border border-gray-200/70 dark:border-slate-600 rounded">
                    <div className="h-5 w-5 rounded-full border-2 border-blue-600 border-t-transparent motion-reduce:animate-none animate-spin" aria-hidden="true" />
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Upload läuft… bitte warten</div>
                  </div>
                </div>
              )}
            </div>
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-slate-600 flex justify-end gap-3 bg-white dark:bg-slate-800">
            <button 
              onClick={onClose} 
              disabled={saving} 
              className="px-4 py-2 bg-gray-200 dark:bg-slate-600 text-gray-900 dark:text-gray-100 rounded hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors"
            >
              Abbrechen
            </button>
            <button 
              onClick={handleSavePDF} 
              disabled={saving || loading} 
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:opacity-50 transition-colors inline-flex items-center gap-2"
            >
                {saving && (
                  <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent motion-reduce:animate-none animate-spin" aria-hidden="true" />
                )}
                {saving ? 'Sende…' : 'An HubSpot senden'}
            </button>
        </div>
      </div>
    </div>
  );
};

export default FormularModal;